import React, { useEffect, useState } from 'react';

import FileUploader from '@/operator/components/general/input/FileUploader';
import {
    StyledAlignedEndInput,
    StyledWidthWrapper,
} from '@/operator/components/modules/AuthPage/Styles';
import {
    dataUrlToFile,
    toBase64,
    updateImageSize,
} from '@/operator/utils/utils';
import { North } from '@mui/icons-material';
import { Button, TextField } from '@mui/material';
import UserAvatar from 'Components/UserAvatar';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import { useOrgNavigate } from "app/routes/useOrgNavigate";
import styled from 'styled-components';
import { useFormHook } from '@/operator/hooks/useFormHook';
import { useAppContext } from '@/operator/context/AppContext';
import Loader from 'Components/Loader';
import { CenterContentContainer } from '@/operator/components/modules/AuthPage/setup/signin/Styles';
import { ROUTES } from 'app/routes/constants';
import useAuth, { AuthProvider } from 'Modules/firebase/use-auth';
import { useFirestoreUserQuery } from 'generated/graphql';

export const ImgContentContainer = styled.div`
    display: flex;
    margin-bottom: 30px;
`;

const MAX_PROFILE_PICTURE_FILE_SIZE_IN_BYTES = Number(
    process.env.NEXT_PUBLIC_MAX_PROFILE_PICTURE_FILE_SIZE_IN_BYTES,
);
const MAX_PROFILE_PICTURE_FILE_SIZE_IN_MB =
    MAX_PROFILE_PICTURE_FILE_SIZE_IN_BYTES / 1024;

const StyledInputsContainer = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 12px;
    @media (width <= 850px) {
        display: flex;
        flex-direction: column;
        gap: 12px;
    }
`;

const formInputs = {
    givenName: 'givenName',
    surName: 'surName',
    preferredName: 'preferredName',
    pronouns: 'pronouns',
};

interface UserProfile {
    email?: string | null;
    email_verified?: boolean | null;
    name?: string | null;
    displayName?: string | null;
    nickname?: string | null;
    picture?: string | null;
    sub?: string | null;
    updated_at?: string | null;
    org_id?: string | null;
    [key: string]: unknown; // Any custom claim which could be in the profile
}

const GoogleSSOProfilePicture = ({ user }: { user: UserProfile }) => (
    <CenterContentContainer
        style={{
            alignItems: 'center',
            flexDirection: 'column',
        }}
    >
        <UserAvatar
            style={{
                width: '160px',
                height: '160px',
                backgroundColor: 'grey',
                margin: '0 auto 0 auto',
                fontSize: 'xxx-large'
            }}
            src={user?.picture}
            alt={user?.name || user?.displayName}
        />

        <North style={{ margin: '0', color: '#999999' }} />

        <Typography
            color="grey"
            margin="0 0 0 0"
            style={{ color: '#999999' }}
            variant="body2"
            align="center"
        >
            Wow, you look great
        </Typography>
    </CenterContentContainer>
);

const EmailPasswordProfilePicture = ({
    avatarContent,
    fileUploadError,
    handleChange,
}: {
    avatarContent: string;
    fileUploadError: string;
    handleChange: (event: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
}) => {
    const { t } = useTranslation();
    return (
        <ImgContentContainer>
            <div style={{ width: '160px' }}>
                <UserAvatar
                    style={{
                        width: '160px',
                        height: '160px',
                        backgroundColor: 'grey',
                        margin: '0 auto 0 auto',
                        fontSize: 'xxx-large'
                    }}
                    src={avatarContent}
                    alt={''}
                />
            </div>
            <FileUploader
                containerStyle={{ width: '100%', paddingRight: 0 }}
                inputStyle={{ backgroundColor: 'white' }}
                label={t(
                    'Auth.Setup.Onboarding.GoogleFormInput.FileInputLabel',
                )}
                subLabel={t(
                    'Auth.Setup.Onboarding.GoogleFormInput.FileInputSubLabel',
                    {
                        maxSizeInMb: MAX_PROFILE_PICTURE_FILE_SIZE_IN_MB,
                    },
                )}
                accept="image/png, image/jpeg"
                error={fileUploadError}
                onChange={handleChange}
            />
        </ImgContentContainer>
    );
};

const OnboardingContent = (): JSX.Element => {
    const { t } = useTranslation();

    const { user, loading } = useAuth();

    const navigate = useOrgNavigate();

    const [fileUrl, setFileUrl] = useState('');
    const [fileUploadError, setFileUploadError] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const userQuery = useFirestoreUserQuery({
        variables: { where: { uid: user?.uid } },
        skip: loading || !user,
    });

    const { givenName, surName, preferredName, pronouns, avatar, avatarName } =
        useAppContext();

    const [givenNameControlled, setGivenNameControlled] = useState<string>(
        givenName || user?.given_name,
    );
    const [surNameControlled, setSurNameControlled] = useState<string>(
        surName || user?.family_name,
    );

    const defaultValues = {
        [formInputs.preferredName]: preferredName,
        [formInputs.pronouns]: pronouns,
    };

    const { isTOSEnabled } = useAppContext();

    const {
        setPreferredName: setPreferredNameContext,
        setPronouns: setPronounsContext,
        setGivenName: setGivenNameContext,
        setSurname: setSurnameContext,
        setAvatar: setAvatarContext,
        setAvatarName: setAvatarNameContext,
    } = useAppContext();

    useEffect(() => {
        if (avatar) {
            const cacheFile = dataUrlToFile(avatar, avatarName);
            setFileUrl(URL.createObjectURL(cacheFile));
        }
    }, []);

    useEffect(() => {
        if (!loading && userQuery.data) {
            setIsLoading(false);
        }
    }, [userQuery.loading, userQuery.data]);

    const { register, getValues } = useFormHook(defaultValues);

    const onSubmit = () => {
        setGivenNameContext(givenNameControlled);
        setSurnameContext(surNameControlled);
        setPreferredNameContext(getValues(formInputs.preferredName));
        setPronounsContext(getValues(formInputs.pronouns));
        if (isTOSEnabled) {
            // @todo: this route doesn't exist
            navigate('/auth/setup/success');
        }
        navigate(ROUTES.AUTH_SETUP_BUILD);
    };

    const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        const encodedFile = (await toBase64(file)) as string;

        if (!event.target.files || !file) {
            return;
        }

        if (file.size / 1000 > MAX_PROFILE_PICTURE_FILE_SIZE_IN_BYTES) {
            setFileUploadError(
                `File size is greater than ${MAX_PROFILE_PICTURE_FILE_SIZE_IN_MB}mb.`,
            );
            return;
        }

        setFileUploadError('');
        setFileUrl(URL.createObjectURL(file));
        setAvatarNameContext(file.name);
        setAvatarContext(encodedFile);
    };

    const avatarContent = fileUrl || updateImageSize(user?.picture ?? '');

    const updatedUser = {
        ...user,
        picture: updateImageSize(user?.picture ?? ''),
    };

    if (isLoading) {
        return <Loader message="Loading..." />;
    }

    return (
        <StyledWidthWrapper>
            {user?.provider === AuthProvider.google ? (
                <GoogleSSOProfilePicture user={updatedUser} />
            ) : (
                <EmailPasswordProfilePicture
                    avatarContent={avatarContent}
                    handleChange={handleChange}
                    fileUploadError={fileUploadError}
                />
            )}
            <br />
            <form
                onSubmit={(event) => {
                    event.preventDefault();
                    onSubmit();
                }}
            >
                <StyledInputsContainer>
                    <TextField
                        value={givenNameControlled}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            setGivenNameControlled(e.target.value);
                        }}
                        sx={{
                            width: '100%',
                            margin: '5px 0px 30px',
                        }}
                        title={t('Auth.Setup.Onboarding.GoogleFormInput.Name')}
                        label={t('Auth.Setup.Onboarding.GoogleFormInput.Name')}
                        error={!givenNameControlled}
                        helperText={
                            !givenNameControlled
                                ? t(
                                    'Auth.Setup.Onboarding.GoogleFormInput.RequiredName',
                                )
                                : undefined
                        }
                    />
                    <TextField
                        value={surNameControlled}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            setSurNameControlled(e.target.value);
                        }}
                        error={!surNameControlled}
                        helperText={
                            !surNameControlled
                                ? t(
                                    'Auth.Setup.Onboarding.GoogleFormInput.RequiredLastName',
                                )
                                : undefined
                        }
                        sx={{
                            width: '100%',
                            margin: '5px 0px 30px',
                        }}
                        label={t(
                            'Auth.Setup.Onboarding.GoogleFormInput.LastName',
                        )}
                        title={t(
                            'Auth.Setup.Onboarding.GoogleFormInput.LastName',
                        )}
                    />
                </StyledInputsContainer>
                <StyledInputsContainer>
                    <TextField
                        {...register(formInputs.preferredName)}
                        sx={{
                            width: '100%',
                            margin: '5px 0px 30px',
                        }}
                        id="Preferred-Name"
                        label={t(
                            'Auth.Setup.Onboarding.GoogleFormInput.PreferredName',
                        )}
                        title={t(
                            'Auth.Setup.Onboarding.GoogleFormInput.PreferredName',
                        )}
                        style={{ margin: '5px 0px 30px' }}
                    />
                    <TextField
                        {...register(formInputs.pronouns)}
                        sx={{
                            width: '100%',
                            margin: '5px 0px 30px',
                        }}
                        id="user-pronouns"
                        label={t(
                            'Auth.Setup.Onboarding.GoogleFormInput.Pronouns',
                        )}
                        title={t(
                            'Auth.Setup.Onboarding.GoogleFormInput.Pronouns',
                        )}
                    />
                </StyledInputsContainer>
                <StyledAlignedEndInput
                    style={{
                        color: 'gray',
                        margin: '5px 0px 30px',
                        width: '100%',
                    }}
                    value={user?.email ?? ''}
                    label={t('CommonWords.Email')}
                    id="user-email"
                    required
                    title={t('CommonWords.Email')}
                    disabled
                />

                <Button
                    variant="purple"
                    style={{ width: '100%' }}
                    type="submit"
                    disabled={!surNameControlled || !givenNameControlled}
                >
                    {t('Auth.Setup.Onboarding.GoogleFormInput.ButtonText')}
                </Button>
            </form>
        </StyledWidthWrapper>
    );
};

export default OnboardingContent;
